// ============================================================
// SALT  - User Profile Page
// Global page: playsalt.gg/profile/:userId
// Wires v2 backend APIs with v1-style layout
// ============================================================

import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import CodeInput from '../components/ui/CodeInput';
import axiosClient from '../api/axiosClient';
import { useAuthContext } from '../context/AuthContext';
import { useToast } from '../hooks/useToast';
import ToastContainer from '../components/ui/Toast';

import '../styles/user-profile.css';
import '../styles/report.css';
import ReportButton from '../components/ui/ReportButton';

// ============================================================
// Types (mirrors v2 backend response shapes)
// ============================================================

type SocialPlatform = 'twitch' | 'twitter' | 'youtube' | 'discord' | 'steam';

interface UserProfileHeader {
  userId: number;
  username: string;
  avatarUrl: string | null;
  avatarHidden: boolean;
  bio: string | null;
  memberSince: string;
  isOwnProfile: boolean;
}

interface UserSocials {
  twitch: string | null;
  twitter: string | null;
  youtube: string | null;
  discord: string | null;
  steam: string | null;
}

interface CompetitiveSummaryEntry {
  gameId: number;
  gameName: string;
  gameCode: string;
  seasonId: number;
  seasonName: string;
  team: {
    teamId: number;
    teamName: string;
    teamTag: string | null;
    logoUrl: string | null;
    role: 'captain' | 'member';
    teamUrl: string;
  } | null;
  ign: string | null;
  record: { wins: number; losses: number };
  matchesPlayed: number;
  points: number;
}

interface UserIgnEntry {
  gameId: number;
  gameName: string;
  gameCode: string;
  ign: string;
  isVerified: boolean;
}

interface RecentMatchEntry {
  matchId: number;
  gameCode: string;
  opponentTeamName: string;
  opponentTeamLogo: string | null;
  userTeamName: string;
  result: 'win' | 'loss';
  score: string;
  completedAt: string;
  matchUrl: string;
}

interface MatchHistoryEntry {
  matchId: number;
  when: string;
  gameCode: string;
  gameName: string;
  team: { teamId: number; teamName: string; teamUrl: string };
  opponent: { teamId: number; teamName: string; teamUrl: string };
  result: 'win' | 'loss';
  score: string;
  matchType: 'available_now' | 'scheduled' | 'direct_challenge';
  matchUrl: string;
}

interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface MatchHistoryResponse {
  matches: MatchHistoryEntry[];
  pagination: PaginationMeta;
}

interface TeamHistoryEntry {
  eventType: 'created' | 'joined' | 'left';
  teamId: number;
  teamName: string;
  teamLogo: string | null;
  gameCode: string;
  gameName: string;
  seasonName: string;
  eventDate: string;
  teamUrl: string;
}

interface TeamHistoryResponse {
  events: TeamHistoryEntry[];
}

interface UserProfileOverviewResponse {
  header: UserProfileHeader;
  socials: UserSocials;
  competitiveSummary: CompetitiveSummaryEntry[];
  igns: UserIgnEntry[];
  recentMatches: RecentMatchEntry[];
}

interface ActiveGameEntry {
  gameId: number;
  gameName: string;
  gameCode: string;
  logoUrl: string | null;
  currentSeasonId: number | null;
  currentSeasonName: string | null;
}

interface ActiveGamesResponse {
  games: ActiveGameEntry[];
}

interface PendingInvite {
  inviteId: number;
  teamId: number;
  teamName: string;
  gameCode?: string;
  gameName?: string;
  createdAt: string;
}

// ============================================================
// Constants
// ============================================================

const TABS = {
  OVERVIEW: 'overview',
  MATCHES: 'matches',
  TEAMS: 'teams',
  ACCOUNT: 'account',
} as const;

type TabKey = (typeof TABS)[keyof typeof TABS];

const IGN_LIMITS = {
  MIN: 3,
  MAX: 32,
  REGEX: /^[a-zA-Z0-9 _-]+$/,
} as const;

const SOCIAL_RULES: Record<
  SocialPlatform,
  { label: string; max: number; regex: RegExp; hint: string }
> = {
  twitch: {
    label: 'Twitch',
    max: 25,
    regex: /^[A-Za-z0-9_]{4,25}$/,
    hint: '4-25 chars. Letters, numbers, underscore.',
  },
  twitter: {
    label: 'X (Twitter)',
    max: 15,
    regex: /^[A-Za-z0-9_]{1,15}$/,
    hint: '1-15 chars. Letters, numbers, underscore.',
  },
  youtube: {
    label: 'YouTube',
    max: 30,
    regex: /^[A-Za-z0-9._ ]{3,30}$/,
    hint: '3-30 chars. Letters, numbers, spaces, dot, underscore.',
  },
  discord: {
    label: 'Discord',
    max: 32,
    regex: /^[A-Za-z0-9._]{2,32}$/,
    hint: '2-32 chars. Letters, numbers, dot, underscore.',
  },
  steam: {
    label: 'Steam',
    max: 32,
    regex: /^[A-Za-z0-9_-]{2,32}$/,
    hint: '2-32 chars. Letters, numbers, underscore, hyphen.',
  },
};

const SOCIAL_PLATFORMS: SocialPlatform[] = ['twitch', 'twitter', 'youtube', 'discord', 'steam'];

const MATCH_HISTORY_PAGE_SIZE = 20;

// ============================================================
// String helpers
// ============================================================

function clampString(raw: string, max: number): string {
  return raw.length <= max ? raw : raw.slice(0, max);
}

function stripControlChars(raw: string): string {
  return raw
    .split('')
    .filter((ch: string) => {
      const code = ch.charCodeAt(0);
      return code >= 32 && code !== 127;
    })
    .join('');
}

function normalizeIgnTyping(raw: string): string {
  return clampString(stripControlChars(raw), IGN_LIMITS.MAX);
}

function normalizeIgnFinalize(raw: string): string {
  const cleaned = stripControlChars(raw).replace(/\s+/g, ' ').trim();
  return clampString(cleaned, IGN_LIMITS.MAX);
}

function normalizeSocialHandle(
  platform: SocialPlatform,
  raw: string,
  options: { finalize?: boolean } = {}
): string {
  let cleaned = stripControlChars(raw).replace(/^@+/, '');

  if (platform === 'youtube') {
    if (options.finalize) cleaned = cleaned.replace(/\s+/g, ' ').trim();
  } else {
    cleaned = cleaned.replace(/\s+/g, '');
  }

  const max = SOCIAL_RULES[platform]?.max ?? 32;
  return clampString(cleaned, max);
}

function validateSocialHandle(
  platform: SocialPlatform,
  raw: string
): { ok: boolean; handle?: string; message?: string } {
  const rule = SOCIAL_RULES[platform];
  const handle = normalizeSocialHandle(platform, raw, { finalize: true });

  if (!handle) return { ok: false, message: 'Handle is required.' };
  if (rule?.regex && !rule.regex.test(handle)) {
    return { ok: false, message: `Invalid ${rule.label} handle. ${rule.hint}` };
  }
  return { ok: true, handle };
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ============================================================
// Component
// ============================================================

const UserProfile: React.FC = () => {
  const { user: authUser, updateUser, logout } = useAuthContext();
  const navigate = useNavigate();
  const { userId: routeUserId } = useParams<{ userId: string }>();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  //---- Core state ----

  const [profile, setProfile] = useState<UserProfileOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<TabKey>(TABS.OVERVIEW);

  // ---- Match history (separate paginated endpoint) ----

  const [matchHistory, setMatchHistory] = useState<MatchHistoryEntry[]>([]);
  const [matchPagination, setMatchPagination] = useState<PaginationMeta | null>(null);
  const [matchPage, setMatchPage] = useState(1);
  const [matchGameFilter, setMatchGameFilter] = useState<number | ''>('');
  const [matchLoading, setMatchLoading] = useState(false);

  // ---- Team history (separate endpoint) ----

  const [teamHistory, setTeamHistory] = useState<TeamHistoryEntry[]>([]);
  const [teamHistoryLoading, setTeamHistoryLoading] = useState(false);

  // ---- Active games (for IGN dropdown) ----

  const [activeGames, setActiveGames] = useState<ActiveGameEntry[]>([]);

  // ---- Pending invites ----

  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);

  // ------ IGN state (multi-game) --------------------------------------------------------------

  const [ignGameId, setIgnGameId] = useState<number | ''>('');
  const [ignInput, setIgnInput] = useState('');
  const [ignSaving, setIgnSaving] = useState(false);
  const [isIgnModalOpen, setIsIgnModalOpen] = useState(false);
  const [pendingIgn, setPendingIgn] = useState('');

  // ---- Social editing state ----

  const [editingSocial, setEditingSocial] = useState<SocialPlatform | null>(null);
  const [socialForm, setSocialForm] = useState({ platform: '' as SocialPlatform, handle: '' });
  const [socialSaving, setSocialSaving] = useState(false);

  // ---- Avatar upload state ----

  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  // -- Bio edit state -------------------------------------------------
  const [bioDraft, setBioDraft] = useState('');
  const [isBioModalOpen, setIsBioModalOpen] = useState(false);
  const [bioSaving, setBioSaving] = useState(false);

  // -- Account settings state -----------------------------------------
  const [emailChangeStep, setEmailChangeStep] = useState<'confirm' | null>(null);
  const [emailVerifyCode, setEmailVerifyCode] = useState('');
  const [emailVerifyError, setEmailVerifyError] = useState('');
  const [isEmailVerifying, setIsEmailVerifying] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // ---- Derived values ----

  const numericRouteUserId = useMemo(() => {
    const id = Number(routeUserId);
    return Number.isNaN(id) ? null : id;
  }, [routeUserId]);

  const isOwnProfile = useMemo(() => {
    if (!authUser || !numericRouteUserId) return false;
    return authUser.userId === numericRouteUserId;
  }, [authUser, numericRouteUserId]);

  const header = profile?.header ?? null;
  const socials = profile?.socials ?? null;
  const competitiveSummary = profile?.competitiveSummary ?? [];
  const igns = useMemo(() => profile?.igns ?? [], [profile]);
  const recentMatches = profile?.recentMatches ?? [];

  const displayUsername = header?.username ?? authUser?.username ?? 'Unknown Player';
  const displayInitials = displayUsername
    .split(' ')
    .map((p: string) => p[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const memberSinceLabel = header?.memberSince ? formatDate(header.memberSince) : null;

  // Figure out the current IGN for the selected game
  const currentIgnForGame = useMemo(() => {
    if (!ignGameId) return '';
    const entry = igns.find((i: UserIgnEntry) => i.gameId === ignGameId);
    return entry?.ign ?? '';
  }, [igns, ignGameId]);

  const isIgnDirty = normalizeIgnFinalize(ignInput) !== currentIgnForGame;

  // ---- Load profile overview ----

  const loadProfile = useCallback(async () => {
    if (!numericRouteUserId) {
      setError('Invalid user ID.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const res = await axiosClient.get<UserProfileOverviewResponse>(
        `/profile/${numericRouteUserId}`
      );

      setProfile(res.data);

      // Initialize IGN editor: default to first game if user has IGNs
      if (res.data.igns.length > 0) {
        setIgnGameId(res.data.igns[0].gameId);
        setIgnInput(res.data.igns[0].ign);
      }
    } catch (err: any) {
      console.error('[UserProfile] Error loading profile:', err);
      setError('Unable to load this player profile right now.');
      toastRef.current.error('Profile Error', 'Unable to load this player profile.');
    } finally {
      setLoading(false);
    }
  }, [numericRouteUserId]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // ---- Load active games (for IGN dropdown) ----

  useEffect(() => {
    if (!isOwnProfile) return;

    axiosClient
      .get<ActiveGamesResponse>('/games/active')
      .then((res) => {
        const games = res.data?.games ?? [];
        setActiveGames(games);

        // If user has no IGNs yet, default the dropdown to first game
        if (ignGameId === '' && games.length > 0) {
          setIgnGameId(games[0].gameId);
        }
      })
      .catch((err) => {
        console.error('[UserProfile] Error loading active games:', err);
      });
  }, [isOwnProfile, ignGameId]);

  // ---- Load pending invites ----

  useEffect(() => {
    if (!isOwnProfile) return;

    axiosClient
      .get('/teams/invites/me')
      .then((res) => {
        const raw = res.data?.data ?? res.data?.invites ?? res.data ?? [];
        const list = Array.isArray(raw) ? raw : [];
        // Map backend snake_case nested shape to frontend flat camelCase
        const mapped: PendingInvite[] = list.map((inv: any) => ({
          inviteId: inv.invite_id ?? inv.inviteId,
          teamId: inv.team?.team_id ?? inv.teamId,
          teamName: inv.team?.team_name ?? inv.teamName ?? 'Unknown Team',
          gameCode: inv.team?.game_code ?? inv.gameCode,
          gameName: inv.team?.game_name ?? inv.gameName,
          createdAt: inv.sent_at ?? inv.createdAt ?? '',
        }));
        setPendingInvites(mapped);
      })
      .catch((err) => {
        console.error('[UserProfile] Error loading invites:', err);
      });
  }, [isOwnProfile]);

  // ---- Load match history (when tab active) ----

  const loadMatchHistory = useCallback(async () => {
    if (!numericRouteUserId) return;

    try {
      setMatchLoading(true);
      const params: Record<string, string | number> = {
        page: matchPage,
        limit: MATCH_HISTORY_PAGE_SIZE,
      };
      if (matchGameFilter) params.gameId = matchGameFilter;

      const res = await axiosClient.get<MatchHistoryResponse>(
        `/profile/${numericRouteUserId}/match-history`,
        { params }
      );

      setMatchHistory(res.data.matches);
      setMatchPagination(res.data.pagination);
    } catch (err: any) {
      console.error('[UserProfile] Error loading match history:', err);
      toastRef.current.error('Match History', 'Unable to load match history.');
    } finally {
      setMatchLoading(false);
    }
  }, [numericRouteUserId, matchPage, matchGameFilter]);

  useEffect(() => {
    if (activeTab === TABS.MATCHES) {
      loadMatchHistory();
    }
  }, [activeTab, loadMatchHistory]);

  // ---- Load team history (when tab active) ----

  const loadTeamHistory = useCallback(async () => {
    if (!numericRouteUserId) return;

    try {
      setTeamHistoryLoading(true);
      const res = await axiosClient.get<TeamHistoryResponse>(
        `/profile/${numericRouteUserId}/team-history`
      );
      setTeamHistory(res.data.events);
    } catch (err: any) {
      console.error('[UserProfile] Error loading team history:', err);
      toastRef.current.error('Team History', 'Unable to load team history.');
    } finally {
      setTeamHistoryLoading(false);
    }
  }, [numericRouteUserId]);

  useEffect(() => {
    if (activeTab === TABS.TEAMS) {
      loadTeamHistory();
    }
  }, [activeTab, loadTeamHistory]);

  // ============================================================
  // Handlers
  // ============================================================

  const handleTabChange = (tab: TabKey) => setActiveTab(tab);

  // ------ IGN handlers (multi-game) --------------------------------------------------------

  const handleIgnGameChange = (gameId: number) => {
    setIgnGameId(gameId);
    const entry = igns.find((i: UserIgnEntry) => i.gameId === gameId);
    setIgnInput(entry?.ign ?? '');
  };

  const handleIgnSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isOwnProfile || !ignGameId) return;

    const normalized = normalizeIgnFinalize(ignInput);
    if (!normalized) {
      toast.error('IGN Error', 'IGN cannot be empty.');
      return;
    }
    if (normalized.length < IGN_LIMITS.MIN) {
      toast.error('IGN Error', `IGN must be at least ${IGN_LIMITS.MIN} characters.`);
      return;
    }
    if (!IGN_LIMITS.REGEX.test(normalized)) {
      toast.error('IGN Error', 'IGN must contain only letters, numbers, spaces, underscores, and hyphens.');
      return;
    }

    setPendingIgn(normalized);
    setIsIgnModalOpen(true);
  };

  const handleIgnModalCancel = () => {
    if (ignSaving) return;
    setIsIgnModalOpen(false);
  };

  const handleIgnModalConfirm = async () => {
    if (!isOwnProfile || !pendingIgn || !ignGameId || !numericRouteUserId) return;

    try {
      setIgnSaving(true);

      await axiosClient.post(`/users/${numericRouteUserId}/igns`, {
        gameId: ignGameId,
        ign: pendingIgn,
      });

      toast.success('IGN Updated', 'Your in-game name has been updated.');

      // Update IGN in local profile state
      setProfile((prev: UserProfileOverviewResponse | null) => {
        if (!prev) return prev;
        const updatedIgns = [...prev.igns];
        const idx = updatedIgns.findIndex((i: UserIgnEntry) => i.gameId === ignGameId);
        if (idx >= 0) {
          updatedIgns[idx] = { ...updatedIgns[idx], ign: pendingIgn };
        } else {
          // New IGN for this game
          const game = activeGames.find((g: ActiveGameEntry) => g.gameId === ignGameId);
          updatedIgns.push({
            gameId: ignGameId as number,
            gameName: game?.gameName ?? '',
            gameCode: game?.gameCode ?? '',
            ign: pendingIgn,
            isVerified: false,
          });
        }

        // Also update competitive summary IGN if applicable
        const updatedSummary = prev.competitiveSummary.map((cs: CompetitiveSummaryEntry) =>
          cs.gameId === ignGameId ? { ...cs, ign: pendingIgn } : cs
        );

        return { ...prev, igns: updatedIgns, competitiveSummary: updatedSummary };
      });

      setIgnInput(pendingIgn);
      setIsIgnModalOpen(false);
    } catch (err: any) {
      console.error('[UserProfile] Error updating IGN:', err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Unable to update IGN right now.';
      toast.error('IGN Error', message);
    } finally {
      setIgnSaving(false);
    }
  };

  // ---- Social handlers ----

  const startEditSocial = (platform: SocialPlatform) => {
    const currentHandle = socials?.[platform] ?? '';
    setEditingSocial(platform);
    setSocialForm({
      platform,
      handle: normalizeSocialHandle(platform, currentHandle, { finalize: true }),
    });
  };

  const cancelEditSocial = () => {
    if (socialSaving) return;
    setEditingSocial(null);
    setSocialForm({ platform: '' as SocialPlatform, handle: '' });
  };

  const saveSocial = async () => {
    if (!isOwnProfile || !numericRouteUserId) return;

    const platform = socialForm.platform;
    const { ok, handle, message } = validateSocialHandle(platform, socialForm.handle);

    if (!ok) {
      toast.error('Social Error', message ?? 'Invalid handle.');
      return;
    }

    try {
      setSocialSaving(true);

      // v2 API: batch update via PUT with platform as key
      const res = await axiosClient.put(`/profile/${numericRouteUserId}/socials`, {
        [platform]: handle,
      });

      const updatedSocials: UserSocials = res.data?.socials ?? res.data;

      setProfile((prev: UserProfileOverviewResponse | null) =>
        prev ? { ...prev, socials: updatedSocials } : prev
      );

      toast.success('Social Updated', `${SOCIAL_RULES[platform].label} handle saved.`);
      cancelEditSocial();
    } catch (err: any) {
      console.error('[UserProfile] saveSocial error:', err);
      toast.error(
        'Social Error',
        err?.response?.data?.message || 'Unable to save social. Please try again.'
      );
    } finally {
      setSocialSaving(false);
    }
  };

  const deleteSocial = async (platform: SocialPlatform) => {
    if (!isOwnProfile || !numericRouteUserId) return;

    try {
      // v2 API: set to null via PUT
      const res = await axiosClient.put(`/profile/${numericRouteUserId}/socials`, {
        [platform]: null,
      });

      const updatedSocials: UserSocials = res.data?.socials ?? res.data;

      setProfile((prev: UserProfileOverviewResponse | null) =>
        prev ? { ...prev, socials: updatedSocials } : prev
      );

      toast.success('Social Removed', `${SOCIAL_RULES[platform].label} handle removed.`);
    } catch (err: any) {
      console.error('[UserProfile] deleteSocial error:', err);
      toast.error('Social Error', 'Unable to remove social.');
    }
  };


  // -- Bio handlers ---------------------------------------------------

  const openBioModal = () => {
    setBioDraft(header?.bio ?? '');
    setIsBioModalOpen(true);
  };

  const handleBioConfirm = async () => {
    if (!numericRouteUserId) return;
    try {
      setBioSaving(true);
      const trimmed = bioDraft.trim();
      await axiosClient.put(`/profile/${numericRouteUserId}/bio`, {
        bio: trimmed || null,
      });
      if (profile) {
        setProfile({
          ...profile,
          header: { ...profile.header, bio: trimmed || null },
        });
      }
      setIsBioModalOpen(false);
      toast.success('Bio Updated', trimmed ? 'Your bio has been updated.' : 'Your bio has been removed.');
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to update bio';
      toast.error('Update Failed', msg);
    } finally {
      setBioSaving(false);
    }
  };
  // ---- Avatar handlers ----

  const handleAvatarClick = () => {
    if (!isOwnProfile) return;
    setIsAvatarModalOpen(true);
  };

  const handleAvatarModalConfirm = () => {
    setIsAvatarModalOpen(false);
    avatarInputRef.current?.click();
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !numericRouteUserId) return;

    // Client-side validation
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Avatar Error', 'Only JPEG, PNG, GIF, and WebP images are allowed.');
      return;
    }
    if (file.size > 1 * 1024 * 1024) {
      toast.error('Avatar Error', 'Avatar must be under 1 MB.');
      return;
    }

    // Show preview immediately
    const previewUrl = URL.createObjectURL(file);
    setAvatarPreview(previewUrl);

    try {
      setAvatarUploading(true);

      const formData = new FormData();
      formData.append('avatar', file);

      const res = await axiosClient.post(
        `/profile/${numericRouteUserId}/avatar`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const newAvatarUrl = res.data?.avatarUrl;

      // Update profile state
      setProfile((prev: UserProfileOverviewResponse | null) =>
        prev
          ? { ...prev, header: { ...prev.header, avatarUrl: newAvatarUrl } }
          : prev
      );

      // Update auth context so navbar avatar updates too
      if (newAvatarUrl) {
        updateUser({ avatarUrl: newAvatarUrl });
      }

      toast.success('Avatar Updated', 'Your profile photo has been updated.');
    } catch (err: any) {
      console.error('[UserProfile] Avatar upload error:', err);
      toast.error(
        'Avatar Error',
        err?.response?.data?.message || err?.response?.data?.error || 'Unable to upload avatar.'
      );
      // Revert preview on failure
      setAvatarPreview(null);
    } finally {
      setAvatarUploading(false);
      // Reset input so same file can be re-selected
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  // ---- Invite handlers ----

  const handleInviteRespond = async (inviteId: number, response: 'accept' | 'decline') => {
    if (!isOwnProfile) return;

    // Find the invite to get gameCode for redirect
    const invite = pendingInvites.find((i: PendingInvite) => i.inviteId === inviteId);

    try {
      const res = await axiosClient.post(`/teams/invites/${inviteId}/respond`, {
        response,
      });

      if (response === 'accept') {
        toast.success('Team Joined', "Invite accepted. You've joined the team!");
        const teamId = res.data?.data?.team?.team_id ?? res.data?.teamId;
        if (teamId) {
          const gc = invite?.gameCode;
          // Check if we're on a game subdomain already
          const host = window.location.hostname.toLowerCase();
          const isOnGameSub =
            /^[a-z0-9]+\.playsalt\.gg$/.test(host) ||
            /^[a-z0-9]+\.localhost/.test(host);

          // Validate gameCode against allowlist before redirecting
          const VALID_GAME_CODES = ['hi', 'h3'];
          if (gc && !isOnGameSub && VALID_GAME_CODES.includes(gc.toLowerCase())) {
            // Bare domain — redirect to the game subdomain
            const subBase = process.env.REACT_APP_SUBDOMAIN_BASE || '.playsalt.gg';
            window.location.href = `${window.location.protocol}//${gc.toLowerCase()}${subBase}/teams/${teamId}`;
          } else {
            navigate(`/teams/${teamId}`);
          }
        }
      } else {
        toast.success('Invite Declined', 'The invite has been declined.');
        setPendingInvites((prev: PendingInvite[]) => prev.filter((i: PendingInvite) => i.inviteId !== inviteId));
      }
    } catch (err: any) {
      console.error(`[UserProfile] Error ${response}ing invite:`, err);
      toast.error(
        'Invite Error',
        err?.response?.data?.message || `Unable to ${response} this invite.`
      );
    }
  };

  // ============================================================
  // Render helpers
  // ============================================================

  const renderTabButton = (tabKey: TabKey, label: string) => {
    const isActive = activeTab === tabKey;
    const classes = ['sl-profile-tab', isActive ? 'sl-profile-tab--active' : '']
      .filter(Boolean)
      .join(' ');

    return (
      <button
        key={tabKey}
        type="button"
        className={classes}
        onClick={() => handleTabChange(tabKey)}
      >
        {label}
      </button>
    );
  };

  // ---- Overview tab ----

  const renderOverview = () => {
    return (
      <div className="sl-profile-section sl-profile-overview">
        <div className="sl-profile-grid">
          {/* Competitive Summary  - stacked per game */}
          <section className="sl-profile-card">
            <h2 className="sl-profile-card-title">Competitive Summary</h2>
            <p className="sl-profile-muted">Game &amp; season snapshots for this player.</p>

            {isOwnProfile && pendingInvites.length > 0 && (
              <div className="sl-profile-invite-block">
                <div className="sl-profile-invite-header">
                  <span className="sl-profile-invite-title">Pending Team Invites</span>
                  <span className="sl-profile-invite-count">
                    {pendingInvites.length} invite{pendingInvites.length !== 1 ? 's' : ''} available
                  </span>
                </div>
                <ul className="sl-profile-invite-list">
                  {pendingInvites.map((invite: PendingInvite) => (
                    <li key={invite.inviteId} className="sl-profile-invite-item">
                      <div className="sl-profile-invite-main">
                        <span className="sl-profile-stat-value">{invite.teamName}</span>
                        {invite.gameName && (
                          <span className="sl-profile-muted sl-profile-invite-meta">
                            {invite.gameName}
                          </span>
                        )}
                        {invite.createdAt && (
                          <span className="sl-profile-muted sl-profile-invite-meta">
                            Invited {formatDate(invite.createdAt)}
                          </span>
                        )}
                      </div>
                      <div className="sl-profile-invite-actions">
                        <button
                          type="button"
                          className="sl-profile-invite-accept"
                          onClick={() => handleInviteRespond(invite.inviteId, 'accept')}
                        >
                          Accept
                        </button>
                        <button
                          type="button"
                          className="sl-profile-invite-decline"
                          onClick={() => handleInviteRespond(invite.inviteId, 'decline')}
                        >
                          Decline
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {competitiveSummary.length > 0 ? (
              competitiveSummary.map((cs: CompetitiveSummaryEntry) => (
                <div key={`${cs.gameId}-${cs.seasonId}`} className="sl-profile-game-box">
                  <p className="sl-profile-game-label">
                    {cs.gameName} &bull; {cs.seasonName}
                  </p>

                  {cs.team ? (
                    <>
                      <div className="sl-profile-stat-row">
                        <span className="sl-profile-stat-label">Current Team</span>
                        <span className="sl-profile-stat-value">
                          {cs.team.teamName}
                          {' - '}
                          {cs.team.role === 'captain' ? (
                            <span className="sl-profile-pill sl-profile-pill--captain">Captain</span>
                          ) : (
                            <span className="sl-profile-pill sl-profile-pill--member">Member</span>
                          )}
                        </span>
                      </div>

                      <div className="sl-profile-stat-row">
                        <span className="sl-profile-stat-label">IGN</span>
                        <span className="sl-profile-stat-value">{cs.ign || 'Not set'}</span>
                      </div>

                      <div className="sl-profile-stat-row">
                        <span className="sl-profile-stat-label">Record</span>
                        <span className="sl-profile-stat-value">
                          {cs.record.wins} - {cs.record.losses}
                        </span>
                      </div>

                      <div className="sl-profile-stat-row">
                        <span className="sl-profile-stat-label">Matches Played</span>
                        <span className="sl-profile-stat-value">{cs.matchesPlayed}</span>
                      </div>

                      <div className="sl-profile-stat-row">
                        <span className="sl-profile-stat-label">Points</span>
                        <span className="sl-profile-stat-value">
                          {typeof cs.points === 'number' ? cs.points.toFixed(2) : '-'}
                        </span>
                      </div>

                      <div className="sl-profile-actions">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => navigate(cs.team!.teamUrl)}
                        >
                          View Team Profile
                        </Button>
                      </div>
                    </>
                  ) : (
                    <p className="sl-profile-muted sl-profile-team-status">
                      Not currently on a team for this game/season.
                    </p>
                  )}
                </div>
              ))
            ) : (
              <>
                <p className="sl-profile-muted sl-profile-team-status">
                  This player is not currently on a team for any active season.
                </p>

                {isOwnProfile && (
                  <div className="sl-profile-actions" style={{ marginTop: '0.75rem' }}>
                    <Button variant="primary" size="sm" onClick={() => navigate('/create-team')}>
                      Create a Team
                    </Button>
                  </div>
                )}
              </>
            )}
          </section>

          {/* Recent Matches */}
          <section className="sl-profile-card">
            <h2 className="sl-profile-card-title">Recent Matches</h2>
            {recentMatches.length > 0 ? (
              <ul className="sl-profile-matchlist">
                {recentMatches.slice(0, 5).map((m: RecentMatchEntry) => {
                  const completedDate = formatDate(m.completedAt);
                  return (
                    <li
                      key={m.matchId}
                      className="sl-profile-matchlist-item sl-profile-matchlist-item--clickable"
                      onClick={() => navigate(m.matchUrl)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && navigate(m.matchUrl)}
                    >
                      <div className="sl-profile-matchlist-main">
                        <span className="sl-profile-match-opponent">
                          vs {m.opponentTeamName}
                        </span>
                        <span className="sl-profile-match-score">{m.score}</span>
                      </div>
                      <div className="sl-profile-matchlist-meta">
                        <span className="sl-profile-match-date">{completedDate}</span>
                        <span
                          className={`sl-profile-chip ${
                            m.result === 'win'
                              ? 'sl-profile-chip--win'
                              : 'sl-profile-chip--loss'
                          }`}
                        >
                          {m.result === 'win' ? 'Win' : 'Loss'}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="sl-profile-muted">No recent matches.</p>
            )}
          </section>
        </div>

        {/* Socials */}
        <section className="sl-profile-card sl-profile-socials-card">
          <h2 className="sl-profile-card-title">Socials</h2>
          <div className="sl-profile-socials-list">
            {SOCIAL_PLATFORMS.filter((platform: SocialPlatform) => {
              if (isOwnProfile) return true;
              return Boolean(socials?.[platform]);
            }).map((platform: SocialPlatform) => {
              const handle = socials?.[platform] ?? null;
              const isEditing = editingSocial === platform;
              const rule = SOCIAL_RULES[platform];

              return (
                <div key={platform} className="sl-profile-social-row">
                  <div className="sl-profile-social-left">
                    <span className="sl-profile-social-label">{rule.label}</span>
                    {!isEditing && handle && (
                      <span className="sl-profile-social-handle">{handle}</span>
                    )}
                    {!isEditing && !handle && isOwnProfile && (
                      <span className="sl-profile-muted">Not set</span>
                    )}
                  </div>

                  <div className="sl-profile-social-actions">
                    {!isEditing ? (
                      isOwnProfile && (
                        <>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => startEditSocial(platform)}
                          >
                            {handle ? 'Edit' : 'Add'}
                          </Button>
                          {handle && (
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => deleteSocial(platform)}
                            >
                              Remove
                            </Button>
                          )}
                        </>
                      )
                    ) : (
                      <>
                        <input
                          type="text"
                          className="sl-profile-ign-input"
                          placeholder="Handle"
                          value={socialForm.handle}
                          maxLength={rule.max}
                          onChange={(e) => {
                            const next = normalizeSocialHandle(platform, e.target.value);
                            setSocialForm((prev) => ({ ...prev, handle: next }));
                          }}
                          onBlur={() => {
                            const normalized = normalizeSocialHandle(
                              platform,
                              socialForm.handle,
                              { finalize: true }
                            );
                            setSocialForm((prev) => ({ ...prev, handle: normalized }));
                          }}
                        />
                        <Button size="sm" onClick={saveSocial} disabled={socialSaving}>
                          Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={cancelEditSocial}>
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* In-Game Names */}
        {igns.length > 0 && (
          <section className="sl-profile-card sl-profile-ign-display-card">
            <h2 className="sl-profile-card-title">In-Game Names</h2>
            <div className="sl-profile-ign-display-list">
              {igns.map((entry: UserIgnEntry) => (
                <div key={entry.gameId} className="sl-profile-ign-display-item">
                  <span className="sl-profile-ign-game-badge">{entry.gameCode.toUpperCase()}</span>
                  <div className="sl-profile-ign-display-info">
                    <span className="sl-profile-ign-display-game">{entry.gameName}</span>
                    <span className="sl-profile-ign-display-name">{entry.ign}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
        {/* IGN Update  - multi-game */}
        {isOwnProfile && (
          <section className="sl-profile-card sl-profile-ign-card">
            <h2 className="sl-profile-card-title">Update In-Game Name</h2>
            <p className="sl-profile-muted">
              {ignGameId
                ? `Set your IGN for ${activeGames.find((g: ActiveGameEntry) => g.gameId === ignGameId)?.gameName ?? 'the selected game'}.`
                : 'Select a game to set your in-game name.'}
            </p>
            <p className="sl-profile-muted sl-profile-ign-cooldown">
              IGN updates are limited to once every 24 hours per game.
            </p>

            <form onSubmit={handleIgnSave} className="sl-profile-ign-form">
              {/* Game selector */}
              <select
                className="sl-profile-ign-input sl-profile-ign-select"
                value={ignGameId}
                onChange={(e) => handleIgnGameChange(Number(e.target.value))}
              >
                <option value="" disabled>
                  Select Game
                </option>
                {activeGames.map((game: ActiveGameEntry) => (
                  <option key={game.gameId} value={game.gameId}>
                    {game.gameName}
                  </option>
                ))}
              </select>

              {/* IGN input */}
              <input
                type="text"
                className="sl-profile-ign-input"
                placeholder="Enter your IGN"
                value={ignInput}
                maxLength={IGN_LIMITS.MAX}
                onChange={(e) => setIgnInput(normalizeIgnTyping(e.target.value))}
                onBlur={() => setIgnInput((prev: string) => normalizeIgnFinalize(prev))}
                disabled={!ignGameId}
              />

              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={!isIgnDirty || ignSaving || !ignGameId}
                isLoading={ignSaving}
              >
                Save IGN
              </Button>
            </form>
          </section>
        )}
      </div>
    );
  };

  // ---- Match History tab ----

  const renderMatches = () => {
    return (
      <div className="sl-profile-section">

        {/* Game filter */}
        {activeGames.length > 1 && (
          <div className="sl-profile-match-filter">
            <select
              className="sl-profile-ign-input sl-profile-ign-select"
              value={matchGameFilter}
              onChange={(e) => {
                setMatchGameFilter(e.target.value ? Number(e.target.value) : '');
                setMatchPage(1);
              }}
            >
              <option value="">All Games</option>
              {activeGames.map((game: ActiveGameEntry) => (
                <option key={game.gameId} value={game.gameId}>
                  {game.gameName}
                </option>
              ))}
            </select>
          </div>
        )}

        {matchLoading && matchHistory.length === 0 ? (
          <p className="sl-profile-muted">Loading match history...</p>
        ) : matchHistory.length === 0 ? (
          <p className="sl-profile-muted">This player has no recorded matches yet.</p>
        ) : (
          <>
            <div className="sl-profile-table">
              <div className="sl-profile-table-head">
                <div>When</div>
                <div>Team</div>
                <div>Opponent</div>
                <div>Result</div>
                <div>Series</div>
                <div>Type</div>
              </div>
              <div className="sl-profile-table-body">
                {matchHistory.map((m: MatchHistoryEntry) => {
                  const whenLabel = m.when ? formatDateTime(m.when) : '-';
                  const matchType = m.matchType?.replace(/_/g, ' ') ?? 'Match';

                  let resultLabel = 'Result';
                  let resultClass = 'sl-profile-chip';
                  if (m.result === 'win') {
                    resultLabel = 'Win';
                    resultClass += ' sl-profile-chip--win';
                  } else if (m.result === 'loss') {
                    resultLabel = 'Loss';
                    resultClass += ' sl-profile-chip--loss';
                  }

                  return (
                    <div
                      key={m.matchId}
                      className="sl-profile-table-row sl-profile-table-row--clickable"
                      onClick={() => navigate(m.matchUrl)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && navigate(m.matchUrl)}
                    >
                      <div>{whenLabel}</div>
                      <div>{m.team.teamName}</div>
                      <div>{m.opponent.teamName}</div>
                      <div>
                        <span className={resultClass}>{resultLabel}</span>
                      </div>
                      <div>{m.score || '-'}</div>
                      <div>{matchType}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Pagination */}
            {matchPagination && matchPagination.totalPages > 1 && (
              <div className="sl-profile-pagination">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!matchPagination.hasPrevPage || matchLoading}
                  onClick={() => setMatchPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <span className="sl-profile-pagination-info">
                  Page {matchPagination.page} of {matchPagination.totalPages}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!matchPagination.hasNextPage || matchLoading}
                  onClick={() => setMatchPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // ---- Teams tab ----

  const renderTeams = () => {
    if (teamHistoryLoading && teamHistory.length === 0) {
      return (
        <div className="sl-profile-section">
          <p className="sl-profile-muted">Loading team history...</p>
        </div>
      );
    }

    if (teamHistory.length === 0) {
      return (
        <div className="sl-profile-section">
          <p className="sl-profile-muted">No team history recorded for this player yet.</p>
          {isOwnProfile && (
            <div className="sl-profile-actions">
              <Button variant="primary" size="sm" onClick={() => navigate('/create-team')}>
                Create a Team
              </Button>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="sl-profile-section">
        <div className="sl-profile-table">
          <div className="sl-profile-table-head sl-profile-table-head--teams">
            <div>Team</div>
            <div>Game</div>
            <div>Season</div>
            <div>Event</div>
            <div>Date</div>
            <div></div>
          </div>
          <div className="sl-profile-table-body">
            {teamHistory.map((t: TeamHistoryEntry, idx: number) => {
              const dateLabel = t.eventDate ? formatDate(t.eventDate) : '-';

              let eventLabel = t.eventType.charAt(0).toUpperCase() + t.eventType.slice(1);
              let eventClass = 'sl-profile-chip';
              if (t.eventType === 'created' || t.eventType === 'joined') {
                eventClass += ' sl-profile-chip--win';
              } else if (t.eventType === 'left') {
                eventClass += ' sl-profile-chip--loss';
              }

              return (
                <div
                  key={`${t.teamId}-${t.eventType}-${idx}`}
                  className="sl-profile-table-row"
                >
                  <div>{t.teamName}</div>
                  <div>{t.gameName}</div>
                  <div>{t.seasonName}</div>
                  <div>
                    <span className={eventClass}>{eventLabel}</span>
                  </div>
                  <div>{dateLabel}</div>
                  <div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(t.teamUrl)}
                    >
                      View
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // ---- Account tab ----

  const renderAccount = () => {
    if (!isOwnProfile) {
      return (
        <div className="sl-profile-section">
          <h2 className="sl-profile-section-title">Account</h2>
          <p className="sl-profile-muted">
            Account settings are only visible to the account owner.
          </p>
        </div>
      );
    }

    const displayEmail = authUser?.email || null;

    return (
      <div className="sl-profile-section sl-profile-account-section">

        <div className="sl-profile-card">
          <h3 className="sl-profile-card-title">Account Details</h3>
          <div className="sl-profile-stat-row">
            <span className="sl-profile-stat-label">Username</span>
            <span className="sl-profile-stat-value">{displayUsername}</span>
          </div>
          {displayEmail && (
            <div className="sl-profile-stat-row">
              <span className="sl-profile-stat-label">Email</span>
              <span className="sl-profile-stat-value">{displayEmail}</span>
            </div>
          )}
          {memberSinceLabel && (
            <div className="sl-profile-stat-row">
              <span className="sl-profile-stat-label">Member Since</span>
              <span className="sl-profile-stat-value">{memberSinceLabel}</span>
            </div>
          )}
        </div>

        {/* Change Password */}
        <div className="sl-profile-card">
          <h3 className="sl-profile-card-title">Change Password</h3>
          <p className="sl-profile-muted">
            Password must be at least 8 characters with uppercase, lowercase, number, and special character.
          </p>
          <form
            className="sl-profile-account-form"
            onSubmit={async (e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const fd = new FormData(form);
              const currentPassword = fd.get('currentPassword') as string;
              const newPassword = fd.get('newPassword') as string;
              const confirmPassword = fd.get('confirmPassword') as string;

              if (!currentPassword || !newPassword) {
                toast.error('Password Error', 'Please fill in all fields.');
                return;
              }
              if (newPassword !== confirmPassword) {
                toast.error('Password Error', 'New passwords do not match.');
                return;
              }
              if (newPassword.length < 8) {
                toast.error('Password Error', 'Password must be at least 8 characters.');
                return;
              }
              if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])/.test(newPassword)) {
                toast.error('Password Error', 'Password must include uppercase, lowercase, number, and special character.');
                return;
              }

              try {
                await axiosClient.post('/change-password', { currentPassword, newPassword });
                toast.success('Password Changed', 'Your password has been updated.');
                form.reset();
              } catch (err: any) {
                const msg = err?.response?.data?.message || err?.response?.data?.error || 'Failed to change password.';
                toast.error('Password Error', msg);
              }
            }}
          >
            <input
              type="password"
              name="currentPassword"
              placeholder="Current Password"
              className="sl-profile-ign-input"
              autoComplete="current-password"
              required
            />
            <input
              type="password"
              name="newPassword"
              placeholder="New Password"
              className="sl-profile-ign-input"
              autoComplete="new-password"
              required
            />
            <input
              type="password"
              name="confirmPassword"
              placeholder="Confirm New Password"
              className="sl-profile-ign-input"
              autoComplete="new-password"
              required
            />
            <Button type="submit" variant="primary" size="sm">
              Update Password
            </Button>
          </form>
        </div>

        {/* Change Email */}
        <div className="sl-profile-card">
          <h3 className="sl-profile-card-title">Change Email</h3>
          <p className="sl-profile-muted">
            A verification code will be sent to your new email. You'll have 1 hour to confirm.
          </p>

          <form
            className="sl-profile-account-form"
            onSubmit={async (e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const fd = new FormData(form);
              const newEmail = (fd.get('new-email-addr') as string)?.trim();
              const password = fd.get('email-pwd') as string;

              if (!newEmail || !password) {
                toast.error('Email Error', 'Please fill in all fields.');
                return;
              }

              try {
                await axiosClient.post('/change-email', { newEmail, password });
                toast.success('Code Sent', 'A verification code has been sent to your new email.');
                setEmailVerifyCode('');
                setEmailVerifyError('');
                setEmailChangeStep('confirm');
              } catch (err: any) {
                const msg = err?.response?.data?.message || err?.response?.data?.error || 'Failed to request email change.';
                toast.error('Email Error', msg);
              }
            }}
          >
            <input
              type="text"
              name="new-email-addr"
              placeholder="New Email Address"
              className="sl-profile-ign-input"
              autoComplete="off"
              data-lpignore="true"
              data-1p-ignore="true"
              inputMode="email"
              required
            />
            <input
              type="password"
              name="email-pwd"
              placeholder="Current Password"
              className="sl-profile-ign-input"
              autoComplete="new-password"
              data-lpignore="true"
              data-1p-ignore="true"
              required
            />
            <Button type="submit" variant="primary" size="sm">
              Send Verification Code
            </Button>
          </form>
        </div>

        {/* Email Change Verification Modal */}
        <Modal
          isOpen={emailChangeStep === 'confirm'}
          title="Verify Email Change"
          onClose={() => {
            setEmailChangeStep(null);
            setEmailVerifyCode('');
            setEmailVerifyError('');
          }}
          onCancel={() => {
            setEmailChangeStep(null);
            setEmailVerifyCode('');
            setEmailVerifyError('');
          }}
          cancelLabel="Cancel"
          confirmLabel={isEmailVerifying ? 'Verifying...' : 'Confirm Email Change'}
          onConfirm={async () => {
            if (!emailVerifyCode || emailVerifyCode.length !== 6) {
              setEmailVerifyError('Please enter the 6-digit verification code.');
              return;
            }

            setEmailVerifyError('');
            setIsEmailVerifying(true);

            try {
              await axiosClient.post('/confirm-email-change', { code: emailVerifyCode });
              toast.success('Email Updated', 'Your email has been changed successfully.');
              setEmailChangeStep(null);
              setEmailVerifyCode('');
              setEmailVerifyError('');
              loadProfile();
            } catch (err: any) {
              const msg = err?.response?.data?.message || err?.response?.data?.error || 'Failed to confirm email change.';
              setEmailVerifyError(msg);
            } finally {
              setIsEmailVerifying(false);
            }
          }}
          disableConfirm={emailVerifyCode.length < 6 || isEmailVerifying}
        >
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '16px' }}>
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <rect x="4" y="10" width="40" height="28" rx="4" stroke="#c0981d" strokeWidth="2" />
                <polyline points="4,14 24,28 44,14" stroke="#c0981d" strokeWidth="2" fill="none" />
                <circle cx="38" cy="14" r="8" fill="#4ade80" stroke="#171717" strokeWidth="2" />
                <path d="M35 14l2 2 4-4" stroke="#171717" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            <p style={{ fontSize: '0.85rem', color: '#a3a3a3', marginBottom: '20px' }}>
              Enter the 6-digit code sent to your new email address.
            </p>

            <CodeInput
              value={emailVerifyCode}
              onChange={(val) => {
                setEmailVerifyCode(val);
                setEmailVerifyError('');
              }}
              error={emailVerifyError}
              disabled={isEmailVerifying}
            />
          </div>
        </Modal>

        {/* Danger Zone: Delete Account */}
        <div className="sl-profile-card sl-profile-card--danger">
          <h3 className="sl-profile-card-title sl-profile-card-title--danger">Danger Zone</h3>
          <p className="sl-profile-muted">
            Deleting your account will disable it immediately. You'll have 7 days to recover
            by logging in. After 7 days, your data will be permanently deleted. You must transfer
            or disband any teams where you are captain before deleting.
          </p>
          {!showDeleteConfirm ? (
            <div className="sl-profile-actions">
              <Button
                variant="ghost"
                size="sm"
                className="sl-profile-btn--danger"
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete Account
              </Button>
            </div>
          ) : (
            <form
              className="sl-profile-account-form"
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const fd = new FormData(form);
                const password = fd.get('deletePassword') as string;

                if (!password) {
                  toast.error('Delete Error', 'Please enter your password to confirm.');
                  return;
                }

                try {
                  const res = await axiosClient.post('/delete-account', { password });
                  const deletionDate = res.data?.data?.deletionDate;
                  toast.success(
                    'Account Scheduled for Deletion',
                    deletionDate
                      ? `Your account will be permanently deleted on ${formatDate(deletionDate)}. Log in within 7 days to recover.`
                      : 'Your account will be permanently deleted in 7 days. Log in to recover.'
                  );
                  setShowDeleteConfirm(false);

                  // Log out and redirect to auth page after a brief delay
                  setTimeout(() => {
                    logout();
                    navigate('/auth', { replace: true });
                  }, 2500);
                } catch (err: any) {
                  const msg = err?.response?.data?.message || err?.response?.data?.error || 'Failed to delete account.';
                  toast.error('Delete Error', msg);
                }
              }}
            >
              <p className="sl-profile-muted sl-profile-delete-warning">
                This action cannot be undone after 7 days. Enter your password to confirm.
              </p>
              <input
                type="password"
                name="deletePassword"
                placeholder="Enter your password"
                className="sl-profile-ign-input"
                autoComplete="current-password"
                required
              />
              <div className="sl-profile-account-form-actions">
                <Button
                  type="submit"
                  variant="ghost"
                  size="sm"
                  className="sl-profile-btn--danger"
                >
                  Confirm Delete
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  };

  // ============================================================
  // Loading / error states
  // ============================================================

  if (loading && !profile && !error) {
    return (
      <div className="sl-dashboard">
        <div className="sl-dashboard-inner sl-profile-page">
          <p className="sl-profile-muted">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile && !loading && error) {
    return (
      <div className="sl-dashboard">
        <div className="sl-dashboard-inner sl-profile-page">
          <p className="sl-profile-error">{error}</p>
        </div>
      </div>
    );
  }

  if (!profile && !loading && !error) {
    return (
      <div className="sl-dashboard">
        <div className="sl-dashboard-inner sl-profile-page">
          <p className="sl-profile-muted">This profile could not be loaded.</p>
        </div>
      </div>
    );
  }

  // ============================================================
  // IGN Modal
  // ============================================================

  const selectedGameName =
    activeGames.find((g: ActiveGameEntry) => g.gameId === ignGameId)?.gameName ?? 'this game';

  const ignModalMessage = `You're about to update your in-game name for ${selectedGameName}.

Current IGN: ${currentIgnForGame || 'Not set - '}
New IGN: ${pendingIgn || normalizeIgnFinalize(ignInput) || 'Not set'}`;

  // ============================================================
  // Main render
  // ============================================================

  const avatarSrc = header?.avatarHidden
    ? null
    : (avatarPreview || header?.avatarUrl || null);

  return (
    <>
      <div className="sl-dashboard">
        <div className="sl-dashboard-inner sl-profile-page">
          {/* Header Card */}
          <section className="sl-card sl-profile-header-card">
            <div className="sl-profile-header-top">
              <div className="sl-profile-avatar-block">
                <div
                  className={`sl-profile-avatar-circle ${isOwnProfile ? 'sl-profile-avatar-circle--editable' : ''}`}
                  onClick={handleAvatarClick}
                  role={isOwnProfile ? 'button' : undefined}
                  tabIndex={isOwnProfile ? 0 : undefined}
                  aria-label={isOwnProfile ? 'Change avatar' : undefined}
                >
                  {avatarSrc ? (
                    <img
                      className="sl-profile-avatar-img"
                      src={avatarSrc}
                      alt={`${displayUsername} avatar`}
                    />
                  ) : (
                    <span className="sl-profile-avatar-initials">{displayInitials}</span>
                  )}

                  {/* Upload overlay for own profile */}
                  {isOwnProfile && (
                    <div className="sl-profile-avatar-overlay">
                      {avatarUploading ? (
                        <svg className="sl-btn-spinner" width="20" height="20" viewBox="0 0 16 16" fill="none">
                          <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" opacity="0.25" />
                          <path d="M8 2a6 6 0 015.92 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
                          <circle cx="12" cy="13" r="4" />
                        </svg>
                      )}
                    </div>
                  )}
                </div>

                {/* Hidden file input */}
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="sl-profile-avatar-file-input"
                  onChange={handleAvatarChange}
                  aria-hidden="true"
                  tabIndex={-1}
                />
                {isOwnProfile && (
                  <button
                    type="button"
                    className="sl-profile-avatar-change"
                    onClick={handleAvatarClick}
                  >
                    Change Photo
                  </button>
                )}
              </div>

              <div className="sl-profile-header-main">
                <div className="sl-profile-header-topline">
                  <h1 className="sl-profile-username">{displayUsername}</h1>
                  {isOwnProfile && (
                    <span className="sl-profile-pill sl-profile-pill--you">You</span>
                  )}
                </div>

                <div className="sl-profile-meta">
                  {memberSinceLabel && (
                    <span className="sl-profile-meta-item">
                      Member Since:{' '}
                      <span className="sl-profile-meta-strong">{memberSinceLabel}</span>
                    </span>
                  )}
                </div>
              </div>

              {/* Right side: bio */}
              {(header?.bio || isOwnProfile) && (
                <div className="sl-profile-header-side">
                  <div className="sl-team-bio-box">
                    <p className="sl-team-bio-text">
                      {header?.bio || (isOwnProfile ? 'No bio yet.' : '')}
                      {isOwnProfile && (
                        <button
                          type="button"
                          className="sl-team-bio-edit"
                          onClick={openBioModal}
                          title="Edit bio"
                        >
                          &#9998;
                        </button>
                      )}
                    </p>
                  </div>
                </div>
              )}
            </div> 

            {/* Desktop tabs */}
            <div className="sl-profile-tabs">
              {renderTabButton(TABS.OVERVIEW, 'Overview')}
              {renderTabButton(TABS.MATCHES, 'Match History')}
              {renderTabButton(TABS.TEAMS, 'Teams')}
              {isOwnProfile && renderTabButton(TABS.ACCOUNT, 'Account')}
              {!isOwnProfile && authUser && (
                <div className="sl-profile-header-report">
                  <ReportButton
                    targetId={numericRouteUserId!}
                    reportableContent={[
                      ...(header?.avatarUrl && !header?.avatarHidden
                        ? [{ type: 'user_avatar' as const, label: 'Profile Photo' }]
                        : []),
                      ...(header?.bio
                        ? [{ type: 'user_bio' as const, label: 'Bio' }]
                        : []),
                    ]}
                  />
                </div>
              )}
            </div>

            {/* Mobile dropdown */}
            <div className="sl-profile-tabs-mobile">
              <select
                className="sl-profile-tabs-select"
                value={activeTab}
                onChange={(e) => handleTabChange(e.target.value as TabKey)}
              >
                <option value={TABS.OVERVIEW}>Overview</option>
                <option value={TABS.MATCHES}>Match History</option>
                <option value={TABS.TEAMS}>Teams</option>
                {isOwnProfile && <option value={TABS.ACCOUNT}>Account</option>}
              </select>
            </div>
          </section>

          {/* ---- Tab content ---- */}
          <div className="sl-profile-tab-content">
            {activeTab === TABS.OVERVIEW && renderOverview()}
            {activeTab === TABS.MATCHES && renderMatches()}
            {activeTab === TABS.TEAMS && renderTeams()}
            {activeTab === TABS.ACCOUNT && renderAccount()}
          </div>
        </div>
      </div>

      {/* ---- IGN Confirmation Modal ---- */}
      <Modal
        isOpen={isIgnModalOpen}
        title="Confirm IGN Update"
        message={ignModalMessage}
        confirmLabel={ignSaving ? 'Saving...' : 'Confirm Update'}
        cancelLabel="Cancel"
        onConfirm={handleIgnModalConfirm}
        onCancel={handleIgnModalCancel}
        disableConfirm={ignSaving}
        disableCancel={ignSaving}
      />

      {/* ---- Toast container ---- */}
      <ToastContainer toasts={toast.toasts} onRemove={toast.removeToast} />

      {/* Avatar Upload Modal */}
      <Modal
        isOpen={isAvatarModalOpen}
        title="Upload Profile Photo"
        onClose={() => setIsAvatarModalOpen(false)}
        onCancel={() => setIsAvatarModalOpen(false)}
        onConfirm={handleAvatarModalConfirm}
        confirmLabel="Choose File"
        cancelLabel="Cancel"
      >
        <div style={{ fontSize: '0.88rem', color: '#d4d4d4', lineHeight: 1.6 }}>
          <p style={{ marginBottom: '0.75rem' }}>
            Your profile photo will be visible to all SALT users. Please review the following requirements before uploading:
          </p>
          <div style={{ background: 'rgba(18, 18, 18, 0.6)', border: '1px solid rgba(163, 163, 163, 0.2)', borderRadius: '0.5rem', padding: '0.75rem 1rem' }}>
            <p style={{ fontWeight: 600, color: '#e5e5e5', marginBottom: '0.5rem' }}>File Requirements</p>
            <p style={{ marginBottom: '0.25rem' }}>Accepted formats: JPEG, PNG, GIF, WebP</p>
            <p style={{ marginBottom: '0.25rem' }}>Maximum file size: 1 MB</p>
            <p style={{ marginBottom: '0.25rem' }}>Maximum dimensions: 500 x 500 px</p>
            <p>Image will be resized to 256 x 256 px</p>
          </div>
          <div style={{ background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.25)', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginTop: '0.65rem' }}>
            <p style={{ fontWeight: 600, color: '#f87171', marginBottom: '0.5rem' }}>Content Policy</p>
            <p>All uploads are automatically scanned for inappropriate content. Images containing nudity, violence, hate symbols, drug references, or other prohibited content will be rejected. Repeated violations may result in account action.</p>
          </div>
        </div>
      </Modal>

      {/* Edit Bio Modal */}
      <Modal
        isOpen={isBioModalOpen}
        title="Edit Bio"
        onClose={() => setIsBioModalOpen(false)}
        onConfirm={handleBioConfirm}
        confirmLabel={bioSaving ? 'Saving...' : 'Save'}
        disableConfirm={bioSaving}
        disableCancel={bioSaving}
      >
        <p className="sl-profile-muted">Write a short bio for your profile. Leave blank to remove.</p>
        <div style={{ marginTop: '0.75rem' }}>
          <textarea
            className="sl-team-textarea"
            placeholder="Tell us about yourself..."
            value={bioDraft}
            onChange={(e) => setBioDraft(e.target.value.slice(0, 200))}
            disabled={bioSaving}
            rows={3}
            maxLength={200}
          />
          <p className="sl-profile-muted" style={{ fontSize: '0.75rem', marginTop: '0.25rem', textAlign: 'right' }}>
            {bioDraft.length}/200
          </p>
        </div>
      </Modal>
    </>
  );
};

export default UserProfile;